import { useRequest } from "../hooks";
import {
  InvoicedVsTimeFilters,
  InvoicedVsTimeSummary,
  InvoiceListItem,
  TimeActivityListItem,
  PaginatedResponse,
} from "../interfaces/interfaces";

export const useInvoicedVsTimeReport = () => {
  const { handleRequest } = useRequest();

  const getSummary = async (
    idClient: string,
    filters: InvoicedVsTimeFilters,
  ): Promise<InvoicedVsTimeSummary | null> => {
    const params = new URLSearchParams();

    if (filters.from) {
      params.append("from", filters.from.toISOString().split("T")[0]);
    }
    if (filters.to) {
      params.append("to", filters.to.toISOString().split("T")[0]);
    }
    if (filters.customerIds && filters.customerIds.length > 0) {
      params.append("customerIds", filters.customerIds.join(","));
    }
    if (filters.employeeIds && filters.employeeIds.length > 0) {
      params.append("employeeIds", filters.employeeIds.join(","));
    }
    if (filters.invoiceStatus) {
      params.append("invoiceStatus", filters.invoiceStatus);
    }
    if (filters.groupBy) {
      params.append("groupBy", filters.groupBy);
    }

    return new Promise((resolve, reject) => {
      handleRequest({
        endpoint: `reports/invoiced-vs-time/summary/${idClient}?${params.toString()}`,
        options: { method: "GET" },
        onSuccess: (res) => {
          resolve(res?.data || null);
        },
        onError: (err) => {
          console.error("Error fetching summary:", err);
          reject(err);
        },
      }).catch(reject);
    });
  };

  const getInvoices = async (
    idClient: string,
    filters: InvoicedVsTimeFilters,
    pagination: { page: number; limit: number; sort?: string; dir?: string },
  ): Promise<PaginatedResponse<InvoiceListItem> | null> => {
    const params = new URLSearchParams();

    if (filters.from) {
      params.append("from", filters.from.toISOString().split("T")[0]);
    }
    if (filters.to) {
      params.append("to", filters.to.toISOString().split("T")[0]);
    }
    if (filters.customerIds && filters.customerIds.length > 0) {
      params.append("customerIds", filters.customerIds.join(","));
    }
    if (filters.invoiceStatus) {
      params.append("invoiceStatus", filters.invoiceStatus);
    }

    params.append("page", pagination.page.toString());
    params.append("limit", pagination.limit.toString());
    if (pagination.sort) {
      params.append("sort", pagination.sort);
    }
    if (pagination.dir) {
      params.append("dir", pagination.dir);
    }

    return new Promise((resolve, reject) => {
      handleRequest({
        endpoint: `reports/invoiced-vs-time/invoices/${idClient}?${params.toString()}`,
        options: { method: "GET" },
        onSuccess: (res) => {
          resolve(res?.data || null);
        },
        onError: (err) => {
          console.error("Error fetching invoices:", err);
          reject(err);
        },
      }).catch(reject);
    });
  };

  const getTimeActivities = async (
    idClient: string,
    filters: InvoicedVsTimeFilters,
    pagination: { page: number; limit: number; sort?: string; dir?: string },
  ): Promise<PaginatedResponse<TimeActivityListItem> | null> => {
    const params = new URLSearchParams();

    if (filters.from) {
      params.append("from", filters.from.toISOString().split("T")[0]);
    }
    if (filters.to) {
      params.append("to", filters.to.toISOString().split("T")[0]);
    }
    if (filters.customerIds && filters.customerIds.length > 0) {
      params.append("customerIds", filters.customerIds.join(","));
    }
    if (filters.employeeIds && filters.employeeIds.length > 0) {
      params.append("employeeIds", filters.employeeIds.join(","));
    }

    params.append("page", pagination.page.toString());
    params.append("limit", pagination.limit.toString());
    if (pagination.sort) {
      params.append("sort", pagination.sort);
    }
    if (pagination.dir) {
      params.append("dir", pagination.dir);
    }

    return new Promise((resolve, reject) => {
      handleRequest({
        endpoint: `reports/invoiced-vs-time/timeactivities/${idClient}?${params.toString()}`,
        options: { method: "GET" },
        onSuccess: (res) => {
          resolve(res?.data || null);
        },
        onError: (err) => {
          console.error("Error fetching time activities:", err);
          reject(err);
        },
      }).catch(reject);
    });
  };

  const getEmployees = async (
    idClient: string,
  ): Promise<{ value: string; label: string }[]> => {
    return new Promise((resolve, reject) => {
      handleRequest({
        endpoint: `reports/invoiced-vs-time/employees/${idClient}`,
        options: { method: "GET" },
        onSuccess: (res) => {
          resolve(res?.data || []);
        },
        onError: (err) => {
          console.error("Error fetching employees:", err);
          reject(err);
        },
      }).catch(reject);
    });
  };

  return {
    getSummary,
    getInvoices,
    getTimeActivities,
    getEmployees,
  };
};
